/**
 * Décompte de réservation du panier.
 *
 * L'échéance est gardée en `sessionStorage` et non recalculée à chaque rendu :
 * le volet est re-rendu à chaque modification du panier, et un décompte qui
 * repartirait de zéro à chaque ajout serait visiblement faux.
 *
 * Le décompte s'efface à l'expiration. Rien ne se produit à ce moment-là côté
 * boutique, donc afficher « expiré » tout en gardant le panier intact
 * contredirait le message à l'écran.
 *
 * Panier vidé, l'échéance est oubliée : le prochain ajout repart de la durée
 * pleine. Décision du principal, 2026-09-11. L'élément n'est rendu que panier
 * non vide (snippets/cart-reservation-timer.liquid), donc son absence dit que
 * le panier est vide.
 */
import { StandardEvents } from '@shopify/events';

const CLE = 'sinzem:cart-reservation-deadline';

function oublierEcheance() {
  try {
    sessionStorage.removeItem(CLE);
  } catch {
    /* rien à nettoyer */
  }
}

// Page ouverte panier vide : une échéance restée d'un panier précédent ne doit
// pas reprendre au premier ajout. Le script est chargé avec le volet panier,
// que le panier soit vide ou non (snippets/cart-drawer.liquid).
if (!document.querySelector('cart-reservation-timer')) oublierEcheance();

// Panier vidé en cours de visite : chaque mise à jour du panier (ajout, retrait,
// quantité) passe par cet événement, dont la promesse aboutit une fois le
// panier à jour. On relit alors le panier au serveur. La disparition de
// l'élément ne suffisait pas : le volet se re-rend en plusieurs temps, et
// l'élément pouvait partir avant que le panier soit vide.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;

  Promise.resolve(promise)
    .then(() => fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`))
    .then((response) => response.json())
    .then((cart) => {
      if (cart.item_count === 0) oublierEcheance();
    })
    .catch(() => {});
});

class CartReservationTimer extends HTMLElement {
  connectedCallback() {
    this.sortie = this.querySelector('[data-countdown]');
    if (!this.sortie) return;

    const minutes = Number(this.dataset.minutes) || 10;
    this.echeance = this.lireEcheance(minutes);

    this.tick = this.tick.bind(this);
    this.tick();
    this.intervalle = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.intervalle);
  }

  lireEcheance(minutes) {
    let stockee;
    try {
      stockee = Number(sessionStorage.getItem(CLE));
    } catch {
      // Navigation privée ou stockage refusé : le décompte reste correct pour
      // la durée de la page, il repart simplement au rechargement.
      stockee = 0;
    }

    if (stockee && stockee > Date.now()) return stockee;

    const echeance = Date.now() + minutes * 60_000;
    try {
      sessionStorage.setItem(CLE, String(echeance));
    } catch {
      /* sans stockage, on garde la valeur en mémoire */
    }
    return echeance;
  }

  tick() {
    const restant = Math.max(0, this.echeance - Date.now());

    if (restant === 0) {
      clearInterval(this.intervalle);
      this.hidden = true;
      try {
        sessionStorage.removeItem(CLE);
      } catch {
        /* rien à nettoyer */
      }
      return;
    }

    const total = Math.round(restant / 1000);
    const mm = String(Math.floor(total / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    this.sortie.textContent = `${mm}:${ss}`;
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
